/**
 * [T1.3] frontend 登录态 token 的唯一取值口。
 *
 * ## 为什么需要这个文件
 *
 * frontend 的登录写的是 `auth_token`（`views/LoginView.vue:192`、
 * `views/AuthCallbackView.vue:46`、`views/RegisterProfileView.vue:37`、
 * `stores/identityCenter.ts:298` 等 5 处）。
 *
 * 但有两处读的是 **`nym_auth_token`**：
 *   · `api/opsStorage.ts:867`
 *   · `services/GatewayService.ts:471`
 *
 * 那是隔壁 `web_console` 应用的键，frontend 里从来没有人写过它。
 * 这两处于是**永远读到 null**，请求以空 Authorization 发出 ——
 * 不报错，只是悄悄降级成未鉴权。
 *
 * 两个应用各有各的正典键本身没问题；出问题的是**字符串字面量散落
 * 在各处**，复制粘贴时跨应用带过去了。所以取值口收敛到这里。
 *
 * ## [D23，2026-09-24] 三个出口**各管什么**（新增调用点请照这张表选）
 *
 * | 出口 | 用途 | 键序（逐字固定） | 现有调用方 |
 * |---|---|---|---|
 * | `getAuthorizationHeaderToken()` | **注入请求头**（唯一正解） | `ops_platform_bearer_token` → `platform_access_token` → `auth_token`，含 `AEGIS-` 命中即删键、`mock_token_` 跳过 | `utils/apiBase.ts:862` |
 * | `getPrimaryUserToken()` | 判「我登录了吗 / 我的身份是不是正典用户」 | 只认 `auth_token` | `composables/useStorageTopology.ts:39`、`composables/useSendSafety.ts:49` |
 * | `getAuthToken()` | **遗留兼容**（旧路径的兜底读） | 正典优先 + 遗留别名 | `api/opsStorage.ts:912`、`services/GatewayService.ts:546` |
 *
 * **规则**：
 * 1. 新代码要发请求 ⇒ 一律 `getAuthorizationHeaderToken()`（**不要**自己拼键序、不要读 localStorage）。
 * 2. 判登录态 / 判"这是不是正典 token" ⇒ `getPrimaryUserToken()`（它**故意不**接受遗留别名：
 *    拿一个 `web_console` 的键当"我登录了"正是当年那条静默降级）。
 * 3. `getAuthToken()` 只给**尚未迁移的旧路径**用；新增调用点若落在这一档，请在 PR 里说明理由
 *    （否则它就变成"谁都能用"的第四种口径）。
 *
 * 为什么把这条写进文件头而不是留在评审记录里：三个函数的**键序不同**，
 * 而"读代码能看见差异"是当时的验收口径（`identity_center_upgrade_spec_v0.4` §7.10）；
 * 现在把"谁该用哪个"也钉在同一处，读这一个文件就能答完。
 */

/** frontend 的正典键。 */
export const AUTH_TOKEN_KEY = 'auth_token';

/**
 * 遗留 / 兄弟应用键，仅作**读降级**，不写。
 */
const LEGACY_READ_KEYS = ['nym_auth_token', 'platform_access_token'] as const;

function readKey(key: string): string | null {
    if (typeof localStorage === 'undefined') return null;
    try {
        return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

/** 读取登录态 token；没有则返回 null（不要返回 '' 冒充成功）。 */
export function getAuthToken(): string | null {
    const canonical = readKey(AUTH_TOKEN_KEY);
    if (canonical) return canonical;

    for (const legacy of LEGACY_READ_KEYS) {
        const v = readKey(legacy);
        if (v) {
            console.warn(
                `[T1.3] 登录态取自遗留键 '${legacy}'，正典键是 '${AUTH_TOKEN_KEY}'。`,
            );
            return v;
        }
    }
    return null;
}

/**
 * 供请求头使用。空登录态返回 `''` 而不是 `'Bearer '` ——
 * 后者会让服务端把"没登录"误判成"token 格式错误"。
 */
export function getAuthHeaderValue(): string {
    const t = getAuthToken();
    return t ? `Bearer ${t}` : '';
}

export function hasAuthToken(): boolean {
    return getAuthToken() !== null;
}

// ─────────────────────────────────────────────────────────────────────────────
// [2026-09-23 IC-53 第二半 / spec §7.10] **按概念的取值函数**（键序逐字照搬自各调用面）
//
// §7.10 量出 7 处取值实现、**键集合与顺序各不相同**。直接统一顺序 = 改变实际发出的 token，
// 那是行为变更。所以这里走另一条路：**把每个"概念"的实现收到一处**，各调用面改为调具名函数，
// 键序逐字不变 ⇒ 行为可证同一，而"谁该用哪个概念"变成一件读代码就能看见的事。
//
// 仓内实际存在三个概念（不是同一个东西，别混）：
//   ① **用户登录令牌**：`auth_token`（`getAuthToken()` / `getPrimaryUserToken()`）
//   ② **运营平台 Bearer**：`ops_platform_bearer_token`（`api/services/opsAuth.ts` 的
//      `resolveOpsBearerToken`；`apiBase` 的请求头注入也**优先**用它）
//   ③ **平台访问令牌**：`platform_access_token`（历史别名，作兜底）
// ─────────────────────────────────────────────────────────────────────────────

/** 只认正典主令牌（不含遗留别名、不读 sessionStorage）—— `useStorageTopology` / `useSendSafety` 的口径。 */
export function getPrimaryUserToken(): string | null {
    if (typeof localStorage === 'undefined') return null;
    try {
        return localStorage.getItem(AUTH_TOKEN_KEY) || null;
    } catch {
        return null;
    }
}

/**
 * `apiBase` 注入 `Authorization` 头时的取值：**运营 Bearer 优先**，再平台访问令牌，最后用户令牌。
 *
 * 两点历史行为照搬：
 *   · `AEGIS-` 前缀的本地随机串**不是真凭据**（`mcp_request_aegis_token` 产出、无身份绑定），
 *     命中即**删键**并继续找 —— 这是 [B0-H6] 的"伪 token 污染"自愈；
 *   · `mock_token_` 前缀同样不作为凭据（但**不删**，它是测试残留）。
 *
 * 返回 `null` 表示没有可用凭据（调用方应**省略** Authorization 头，而不是发 `Bearer ` 空值）。
 */
export function getAuthorizationHeaderToken(): string | null {
    if (typeof localStorage === 'undefined') return null;
    const keys = ['ops_platform_bearer_token', 'platform_access_token', AUTH_TOKEN_KEY];
    for (const k of keys) {
        let v: string | null = null;
        try {
            v = localStorage.getItem(k);
        } catch {
            v = null;
        }
        if (!v) continue;
        if (v.startsWith('AEGIS-')) {
            try {
                localStorage.removeItem(k);
            } catch {
                /* ignore */
            }
            console.warn(`[authToken] 清除伪凭据：'${k}' 里是 AEGIS 本地随机串（无身份绑定）`);
            continue;
        }
        if (v.startsWith('mock_token_')) {
            console.warn(`[authToken] 跳过测试残留 mock_token_*：'${k}'`);
            continue;
        }
        return v;
    }
    return null;
}
